import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Paper, TextField, IconButton, Typography, Avatar, Chip,
    CircularProgress, Fade, Alert, Snackbar, Tooltip, Button, Menu, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SupportIcon from '@mui/icons-material/Support';
import GroupsIcon from '@mui/icons-material/Groups';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import InfoIcon from '@mui/icons-material/Info';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/GetApp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';

import { whoami, getSchools } from '../services/APIClient';

import * as pdfjsLib from 'pdfjs-dist';
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const CHATBOT_API_URL = 'https://d10hdjd22usq3e.cloudfront.net/api/chat';
const FEEDBACK_API_URL = 'https://d10hdjd22usq3e.cloudfront.net/api/feedback';
const TEMPLATE_UPLOAD_URL = 'https://d10hdjd22usq3e.cloudfront.net/api/upload-template';
const MAX_RETRIES = 3;
const REQUEST_TIMEOUT = 60000;
const MAX_TEMPLATE_FILE_SIZE = 10 * 1024 * 1024; // 10MB


const detectRole = (user) => {
    try {
        const teacher = user?.stakeholder || user;
        const rolesRaw = teacher?.additionalroles || '[]';
        let roleArray = [];
        try {
            roleArray = JSON.parse(rolesRaw);
        } catch (e) {
            roleArray = [];
        }
        const normalizedRoles = roleArray.map(r => String(r).toLowerCase().replace(/[^a-z]/g, ''));
        if (normalizedRoles.some(r => r.includes('principal'))) return 'PRINCIPAL';
        if (normalizedRoles.some(r => r.includes('senco'))) return 'SENCO';
        if (normalizedRoles.some(r => r === 'set' || r.includes('setco'))) return 'SET';
        return 'TEACHER';
    } catch (error) {
        return 'TEACHER';
    }
};

const ROLE_CONFIG = {
    TEACHER: {
        label: 'Classroom Teacher',
        color: '#1976d2',
        icon: <SchoolIcon />,
        welcomeMessage: (name, schoolInfo) => `Hello ${name}! I'm ALPACA, your AI assistant for literacy insights.

I have access to all student data for ${schoolInfo.name} through our Knowledge Base.

As a Classroom Teacher, I can help you with:
📊 Student Performance - Individual scores and assessment results
🎯 Grouping - Station teaching and guided reading groups
📝 Activities - Targeted interventions and resources
📈 Progress - Track improvement over time
❌ Error Patterns - Common mistakes to address

How can I help you today?`,
        quickQuestions: [
            "Which students have the lowest scores?",
            "Group my students for station teaching",
            "What are common wrong answer patterns?",
            "Who needs phonics intervention?"
        ]
    },
    PRINCIPAL: {
        label: 'Principal',
        color: '#7b1fa2',
        icon: <SupervisorAccountIcon />,
        welcomeMessage: (name, schoolInfo) => `Hello ${name}! I'm ALPACA, your AI assistant for school-wide literacy insights.

I have access to all data for ${schoolInfo.name}.

As Principal, you can ask me about:
📊 School Performance - Overall literacy trends
📈 Class Analytics - Class-by-class comparison
🎯 Resource Allocation - Where support is needed most
👥 At-Risk Students - Students needing intervention

How can I help you today?`,
        quickQuestions: [
            "Give me a school-wide summary",
            "Compare class performance",
            "Which classes need the most support?",
            "How many students are below benchmark?"
        ]
    },
    SENCO: {
        label: 'SENCO',
        color: '#388e3c',
        icon: <SupportIcon />,
        welcomeMessage: (name, schoolInfo) => `Hello ${name}! I'm ALPACA, your AI assistant for SEN literacy support.

I have access to all student data for ${schoolInfo.name}, including SEN, ESL, and ASD flags.

As SENCO, I can help you with:
🎯 Intervention Planning - Identify students needing support
📊 SEN Students - Track flagged students' performance
📈 Progress Monitoring - Track intervention effectiveness
📝 Error Patterns - Common misconceptions to address

How can I help you today?`,
        quickQuestions: [
            "Which SEN students need intervention?",
            "Show ESL student performance",
            "Who should be referred for assessment?",
            "List students needing Tier 2 support"
        ]
    },
    SET: {
        label: 'SET',
        color: '#f57c00',
        icon: <GroupsIcon />,
        welcomeMessage: (name, schoolInfo) => `Hello ${name}! I'm ALPACA, your AI assistant for intervention support.

I have access to all data for ${schoolInfo.name}, including detailed assessment results and error patterns.

As Special Education Teacher, I can help you with:
🎯 Targeted Interventions - Activities for specific skill gaps
📊 Student Progress - Track improvement over time
📝 Error Analysis - Common wrong answers to address
👥 Grouping - Students with similar needs

How can I help you today?`,
        quickQuestions: [
            "Who needs Tier 2 intervention?",
            "What are common error patterns in Letter Sound?",
            "Group students for intervention",
            "Which students are improving?"
        ]
    }
};

const REPORT_PROMPTS = {
    TEACHER: `Please generate a formal, structured "Classroom Literacy Summary Report" based on the data you have.
Format it beautifully with clear headings and bullet points.
Include:
1. Class Overview (Total students, average scores)
2. Strengths & Successes (What tasks are they doing well on?)
3. Areas of Concern (Specific error patterns or tasks with low scores)
4. Intervention Groupings (List specific students who need Tier 2 or Tier 3 support)
5. Recommended Strategies (Actionable next steps for the classroom)`,

    PRINCIPAL: `Please generate a formal, structured "School-Wide Literacy Summary Report".
Format it beautifully with clear headings and bullet points.
Include:
1. School Overview (Total enrollment, classes)
2. Class-by-Class Comparison (Compare average STEN scores across classes)
3. School-Wide Trends (Common strengths and weaknesses across the school)
4. Resource Allocation Recommendations (Which classes or groups need the most support?)`,

    SENCO: `Please generate a formal, structured "SEN & ESL Support Summary Report".
Format it beautifully with clear headings and bullet points.
Include:
1. Overview of Flagged Students (Total students with SEN/ESL flags)
2. High-Priority Students (Students with flags who are scoring significantly below benchmark)
3. Common Error Patterns among flagged students
4. Recommended Tier 2 & Tier 3 Interventions`,

    SET: `Please generate a formal, structured "Intervention Needs Report".
Format it beautifully with clear headings and bullet points.
Include:
1. High-Priority Caseload (Students across all classes needing immediate intervention)
2. Specific Skill Gaps (Breakdown of students struggling with Phonics, Rhyming, etc.)
3. Recommended Station Teaching Groupings
4. Suggested Evidence-Based Activities for these groups`
};

const AlpacaChatbot = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [schoolName, setSchoolName] = useState('');
    const [userRole, setUserRole] = useState('TEACHER');
    const [contextLoading, setContextLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [rollNumber, setRollNumber] = useState(null);
    const [isListening, setIsListening] = useState(false);
    const [voiceSupported, setVoiceSupported] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);

    // Feedback state
    const [feedback, setFeedback] = useState({});
    const [feedbackDialog, setFeedbackDialog] = useState({ open: false, messageId: null });
    const [feedbackComment, setFeedbackComment] = useState('');

    // Template upload state
    const [templateDialog, setTemplateDialog] = useState(false);
    const [templateFile, setTemplateFile] = useState(null);
    const [templateNotes, setTemplateNotes] = useState('');
    const [templateUploading, setTemplateUploading] = useState(false);
    const templateFileInputRef = useRef(null);

    const messagesEndRef = useRef(null);
    const recognitionRef = useRef(null);
    const fileInputRef = useRef(null);
    const inputRef = useRef(null);

    // Export
    const handleExportClick = (event) => setExportAnchorEl(event.currentTarget);
    const handleExportClose = () => setExportAnchorEl(null);

    const exportToText = () => {
        const chatText = messages.map(m =>
            `${m.type === 'user' ? 'Teacher' : 'ALPACA'} [${new Date(m.timestamp).toLocaleTimeString()}]:\n${m.text}`
        ).join('\n\n----------------------------------------\n\n');
        const blob = new Blob([chatText], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ALPACA_Insights_${new Date().toISOString().split('T')[0]}.doc`;
        link.click();
        handleExportClose();
    };

    const exportToPDF = () => {
        window.print();
        handleExportClose();
    };

    // ============================================
    // FEEDBACK HANDLERS
    // ============================================
    const submitFeedback = async (messageId, rating, comment = '') => {
        try {
            const message = messages.find(m => m.id === messageId);
            const messageIndex = messages.findIndex(m => m.id === messageId);
            const userQuestion = messageIndex > 0 ? messages[messageIndex - 1]?.text : '';

            const feedbackData = {
                message_id: messageId,
                rating: rating,
                comment: comment,
                user_question: userQuestion,
                ai_response: message?.text?.substring(0, 500),
                user_role: userRole,
                teacher_id: userInfo?.id,
                school_roll: rollNumber,
                timestamp: new Date().toISOString()
            };

            setFeedback(prev => ({ ...prev, [messageId]: rating }));

            await fetch(FEEDBACK_API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(feedbackData)
            });

            setSnackbar({
                open: true,
                message: rating === 'up' ? '👍 Thanks for the feedback!' : '👎 Thanks! We\'ll work on improving this.',
                severity: 'success'
            });

        } catch (err) {
            console.error('Failed to submit feedback:', err);
            setSnackbar({
                open: true,
                message: 'Feedback recorded locally',
                severity: 'info'
            });
        }
    };

    const handleThumbsUp = (messageId) => {
        if (feedback[messageId]) return;
        submitFeedback(messageId, 'up');
    };

    const handleThumbsDown = (messageId) => {
        if (feedback[messageId]) return;
        setFeedbackDialog({ open: true, messageId });
    };

    const handleFeedbackDialogClose = () => {
        setFeedbackDialog({ open: false, messageId: null });
        setFeedbackComment('');
    };

    const handleFeedbackDialogSubmit = () => {
        submitFeedback(feedbackDialog.messageId, 'down', feedbackComment);
        handleFeedbackDialogClose();
    };

    const handleFeedbackDialogSkip = () => {
        submitFeedback(feedbackDialog.messageId, 'down', '');
        handleFeedbackDialogClose();
    };

    // Voice recognition
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) return;

        setVoiceSupported(true);
        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = 'en-IE';
        recognition.onstart = () => setIsListening(true);
        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            setInput(prev => prev ? `${prev} ${transcript}` : transcript);
            setIsListening(false);
        };
        recognition.onerror = (event) => {
            setIsListening(false);
            if (event.error === 'not-allowed') {
                setSnackbar({ open: true, message: 'Microphone access denied.', severity: 'warning' });
            }
        };
        recognition.onend = () => setIsListening(false);
        recognitionRef.current = recognition;

        return () => recognitionRef.current?.abort();
    }, []);

    const toggleVoiceInput = () => {
        if (!recognitionRef.current) return;
        if (isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
        } else {
            try { recognitionRef.current.start(); } catch (err) { }
        }
    };

    // Load user info
    const loadUserInfo = async () => {
        setContextLoading(true);
        setError(null);
        try {
            const userResponse = await whoami();
            const user = userResponse?.data || userResponse;
            if (!user?.stakeholder) throw new Error('Invalid user response');

            const role = detectRole(user);
            const schoolid = user.stakeholder.schoolid;
            setUserInfo({ id: user.stakeholder.id, name: user.stakeholder.name, schoolId: schoolid });
            setUserRole(role);

            let schoolName = 'your school';
            try {
                const schoolsResponse = await getSchools();
                const schools = schoolsResponse?.data || schoolsResponse || [];
                const school = schools.find(s => s.id === schoolid);
                if (school?.name) { schoolName = school.name; setSchoolName(school.name); }
                if (school?.rollnumber) setRollNumber(school.rollnumber);
            } catch (schoolErr) {
                console.warn('Could not fetch school data:', schoolErr);
            }

            const roleConfig = ROLE_CONFIG[role] || ROLE_CONFIG.TEACHER;
            setMessages([{ id: Date.now(), type: 'bot', text: roleConfig.welcomeMessage(user.stakeholder.name, { name: schoolName }), timestamp: new Date(), isWelcome: true }]);

        } catch (err) {
            setError(`Failed to load user data: ${err.message}`);
            setUserRole('TEACHER');
            setMessages([{ id: Date.now(), type: 'bot', text: ROLE_CONFIG.TEACHER.welcomeMessage('Teacher', { name: 'your school', totalClasses: null }), timestamp: new Date(), isWelcome: true }]);
        } finally {
            setContextLoading(false);
        }
    };

    useEffect(() => { loadUserInfo(); }, []);
    useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

    // File extraction
    const extractTextFromFile = async (file) => {
        return new Promise(async (resolve, reject) => {
            try {
                if (file.type === 'text/plain') {
                    const reader = new FileReader();
                    reader.onload = (e) => resolve(e.target.result);
                    reader.onerror = (e) => reject(e);
                    reader.readAsText(file);
                } else if (file.type === 'application/pdf') {
                    const arrayBuffer = await file.arrayBuffer();
                    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
                    let fullText = '';
                    for (let i = 1; i <= pdf.numPages; i++) {
                        const page = await pdf.getPage(i);
                        const textContent = await page.getTextContent();
                        fullText += textContent.items.map(item => item.str).join(' ') + '\n';
                    }
                    resolve(fullText);
                } else {
                    reject(new Error("Unsupported file type. Please upload a .txt or .pdf file."));
                }
            } catch (error) {
                reject(error);
            }
        });
    };

    // Send message
    const sendMessage = async (messageText) => {
        const question = messageText || input.trim();
        if (!question && !selectedFile) return;
        if (loading) return;

        let finalMessageToSend = question;
        setLoading(true);

        if (selectedFile) {
            try {
                const fileContext = await extractTextFromFile(selectedFile);
                finalMessageToSend = `I have attached a document. Please read it and answer my question.\n\nDOCUMENT CONTENT:\n${fileContext}\n\nMY QUESTION: ${question || "Please summarize this document."}`;
            } catch (error) {
                setSnackbar({ open: true, message: `Failed to read file: ${error.message}`, severity: 'error' });
                setLoading(false);
                return;
            }
        }

        const displayQuestion = selectedFile ? `📎 [Attached: ${selectedFile.name}]\n${question}` : question;
        setMessages(prev => [...prev, { id: Date.now(), type: 'user', text: displayQuestion, timestamp: new Date() }]);
        setInput('');
        setSelectedFile(null);
        inputRef.current?.focus();

        const isStaging =
            window.location.href.includes("staging") ||
            window.location.href.includes("localhost") ||
            window.location.href.includes("alpaca-assessment-test");

        let lastError = null;
        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
                const response = await fetch(CHATBOT_API_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        message: finalMessageToSend,
                        role: userRole,
                        roll_number: rollNumber,
                        environment: isStaging ? "staging" : "production",
                        teacher_id: userInfo?.id || null,
                    }),
                    signal: controller.signal
                });
                clearTimeout(timeoutId);
                if (!response.ok) throw new Error(`Server error: ${response.status}`);
                const data = await response.json();
                setMessages(prev => [...prev, { id: Date.now() + 1, type: 'bot', text: data.response || 'No response received.', citations: data.citations || [], timestamp: new Date() }]);
                setLoading(false);
                return;
            } catch (err) {
                lastError = err;
                if (err.name === 'AbortError') { lastError = new Error('Request timed out'); break; }
                if (attempt < MAX_RETRIES - 1) await new Promise(r => setTimeout(r, Math.pow(2, attempt) * 1000));
            }
        }

        setMessages(prev => [...prev, { id: Date.now() + 1, type: 'bot', text: `Sorry, I encountered an error: ${lastError?.message}. Please try again.`, isError: true, timestamp: new Date() }]);
        setLoading(false);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
    };

    const handleQuickQuestion = (question) => {
        setInput(question);
        sendMessage(question);
    };

    const handleGenerateReport = () => {
        setMessages([{ id: Date.now(), type: 'bot', text: `Generating a formal automated report... This may take a few seconds as I analyze all the data.`, timestamp: new Date(), isWelcome: true }]);
        sendMessage(REPORT_PROMPTS[userRole] || REPORT_PROMPTS.TEACHER);
    };

    // ============================================
    // TEMPLATE UPLOAD HANDLERS
    // ============================================
    const handleTemplateFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > MAX_TEMPLATE_FILE_SIZE) {
            setSnackbar({ open: true, message: 'File is too large. Please upload a file under 10MB.', severity: 'error' });
            e.target.value = null;
            return;
        }
        setTemplateFile(file);
    };

    const handleTemplateDialogClose = () => {
        setTemplateDialog(false);
        setTemplateFile(null);
        setTemplateNotes('');
        if (templateFileInputRef.current) templateFileInputRef.current.value = null;
    };

    const handleTemplateUpload = async () => {
        if (!templateFile) return;
        setTemplateUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', templateFile);
            formData.append('teacherid', userInfo?.id || '');
            formData.append('role', userRole);
            formData.append('school_roll', rollNumber || '');
            formData.append('notes', templateNotes.trim());

            const response = await fetch(TEMPLATE_UPLOAD_URL, { method: 'POST', body: formData });
            if (!response.ok) throw new Error(`Upload failed: ${response.status}`);

            handleTemplateDialogClose();
            setSnackbar({ open: true, message: 'Thank you! Your template has been uploaded successfully.', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: `Upload failed: ${err.message}. Please try again.`, severity: 'error' });
        } finally {
            setTemplateUploading(false);
        }
    };

    const roleConfig = ROLE_CONFIG[userRole] || ROLE_CONFIG.TEACHER;

    if (contextLoading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4 }}>
                <CircularProgress size={48} />
                <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>Loading...</Typography>
            </Box>
        );
    }

    return (
        <Box className="chatbot-main-wrapper" sx={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '80vh' }}>

            {/* Header */}
            <Paper className="no-print" elevation={2} sx={{ p: 2, borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: roleConfig.color }}>
                        <SmartToyIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h6">ALPACA AI Assistant</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip label={roleConfig.label} size="small" icon={roleConfig.icon} sx={{ bgcolor: roleConfig.color, color: 'white' }} />
                            {schoolName && <Chip label={schoolName} size="small" variant="outlined" icon={<SchoolIcon />} />}
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {messages.length > 1 && (
                        <>
                            <Tooltip title="Export Chat">
                                <IconButton onClick={handleExportClick} color="primary">
                                    <DownloadIcon />
                                </IconButton>
                            </Tooltip>
                            <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={handleExportClose}>
                                <MenuItem onClick={exportToText}>Export as Document (.doc/.txt)</MenuItem>
                                <MenuItem onClick={exportToPDF}>Save as PDF</MenuItem>
                            </Menu>
                        </>
                    )}
                    <Tooltip title="Share a report template with us">
                        <IconButton onClick={() => setTemplateDialog(true)} color="default">
                            <CloudUploadIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Powered by AWS Bedrock Knowledge Base">
                        <IconButton><InfoIcon /></IconButton>
                    </Tooltip>
                </Box>
            </Paper>

            {/* Messages */}
            <Box className="print-area" sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#f5f5f5' }}>
                {messages.map((message) => (
                    <Fade in key={message.id}>
                        <Box sx={{ display: 'flex', justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, maxWidth: '80%' }}>
                                {message.type === 'bot' && (
                                    <Avatar sx={{ bgcolor: message.isError ? 'error.main' : roleConfig.color, width: 32, height: 32 }}>
                                        <SmartToyIcon fontSize="small" />
                                    </Avatar>
                                )}
                                <Box>
                                    <Paper elevation={1} sx={{ p: 2, bgcolor: message.type === 'user' ? '#5dbdd6' : 'white', borderRadius: 2, whiteSpace: 'pre-wrap' }}>
                                        <Typography variant="body2" sx={{ color: message.type === 'user' ? '#ffffff' : 'text.primary', fontWeight: message.type === 'user' ? 'bold' : 'normal' }}>
                                            {message.text}
                                        </Typography>
                                    </Paper>
                                    {message.citations && message.citations.length > 0 && (
                                        <Box className="no-print" sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>📚 Sources:</Typography>
                                            {message.citations.map((citation, idx) => (
                                                <Tooltip key={idx} title={citation.preview || 'Data from Knowledge Base'}>
                                                    <Chip label={citation.source || `Source ${idx + 1}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                                                </Tooltip>
                                            ))}
                                        </Box>
                                    )}
                                    {/* Feedback buttons - only for bot messages, not welcome or error */}
                                    {message.type === 'bot' && !message.isWelcome && !message.isError && (
                                        <Box 
                                            className="no-print" 
                                            sx={{ 
                                                mt: 1, 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: 0.5,
                                                opacity: feedback[message.id] ? 1 : 0.6,
                                                '&:hover': { opacity: 1 },
                                                transition: 'opacity 0.2s'
                                            }}
                                        >
                                            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                                Was this helpful?
                                            </Typography>
                                            <Tooltip title="Yes, this was helpful">
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => handleThumbsUp(message.id)}
                                                    disabled={!!feedback[message.id]}
                                                    sx={{ 
                                                        color: feedback[message.id] === 'up' ? 'success.main' : 'text.secondary',
                                                        bgcolor: feedback[message.id] === 'up' ? 'success.light' : 'transparent',
                                                        '&:hover': { bgcolor: 'success.light' }
                                                    }}
                                                >
                                                    {feedback[message.id] === 'up' ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="No, this could be better">
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => handleThumbsDown(message.id)}
                                                    disabled={!!feedback[message.id]}
                                                    sx={{ 
                                                        color: feedback[message.id] === 'down' ? 'error.main' : 'text.secondary',
                                                        bgcolor: feedback[message.id] === 'down' ? 'error.light' : 'transparent',
                                                        '&:hover': { bgcolor: 'error.light' }
                                                    }}
                                                >
                                                    {feedback[message.id] === 'down' ? <ThumbDownIcon fontSize="small" /> : <ThumbDownOutlinedIcon fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                            {feedback[message.id] && (
                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                    ✓ Feedback recorded
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                                {message.type === 'user' && (
                                    <Avatar sx={{ bgcolor: 'grey.400', width: 32, height: 32 }}>
                                        <PersonIcon fontSize="small" />
                                    </Avatar>
                                )}
                            </Box>
                        </Box>
                    </Fade>
                ))}
                {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Avatar sx={{ bgcolor: roleConfig.color, width: 32, height: 32 }}>
                            <SmartToyIcon fontSize="small" />
                        </Avatar>
                        <Paper elevation={1} sx={{ p: 2, bgcolor: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={20} />
                            <Typography variant="caption" color="text.secondary">Searching Knowledge Base...</Typography>
                        </Paper>
                    </Box>
                )}
                <div ref={messagesEndRef} />
            </Box>

            {/* Quick Questions & Report Generator */}
            {messages.length <= 1 && (
                <Box className="no-print" sx={{ p: 2, bgcolor: '#f0f7ff', borderTop: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Quick questions:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {roleConfig.quickQuestions.map((q, i) => (
                                <Chip key={i} label={q} onClick={() => handleQuickQuestion(q)} clickable variant="outlined" size="small"
                                    sx={{ '&:hover': { bgcolor: roleConfig.color, color: 'white', borderColor: roleConfig.color } }}
                                />
                            ))}
                        </Box>
                    </Box>
                    <Box sx={{ pt: 1, borderTop: '1px dashed #ccc' }}>
                        <Button variant="contained" color="primary" startIcon={<PictureAsPdfIcon />} onClick={handleGenerateReport} disabled={loading} sx={{ borderRadius: 2 }}>
                            Generate Automated Summary Report
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                            Creates a printable overview of your data.
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Input */}
            <Paper className="no-print" elevation={3} sx={{ p: 2, borderRadius: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedFile && (
                        <Box sx={{ px: 1 }}>
                            <Chip icon={<PictureAsPdfIcon />} label={selectedFile.name} onDelete={() => setSelectedFile(null)} color="primary" variant="outlined" />
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <input type="file" accept=".pdf,.doc,.docx,.txt" style={{ display: 'none' }} ref={fileInputRef} onChange={(e) => setSelectedFile(e.target.files[0])} />
                        <Tooltip title="Attach document (PDF/TXT)">
                            <IconButton onClick={() => fileInputRef.current.click()} color={selectedFile ? "primary" : "default"} disabled={loading}>
                                <AttachFileIcon />
                            </IconButton>
                        </Tooltip>
                        <TextField
                            fullWidth
                            inputRef={inputRef}
                            placeholder={isListening ? "🎤 Listening..." : "Ask about student data or attached documents..."}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={isListening}
                            size="small"
                            multiline
                            maxRows={3}
                            sx={{
                                '& .MuiOutlinedInput-root': isListening ? {
                                    borderColor: 'error.main',
                                    animation: 'pulse 1.5s infinite',
                                    '@keyframes pulse': {
                                        '0%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)' },
                                        '70%': { boxShadow: '0 0 0 6px rgba(211, 47, 47, 0)' },
                                        '100%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' }
                                    }
                                } : {}
                            }}
                        />
                        {voiceSupported && (
                            <Tooltip title={isListening ? "Stop listening" : "Voice input (Irish English)"}>
                                <IconButton onClick={toggleVoiceInput} disabled={loading}
                                    sx={{ color: isListening ? 'error.main' : 'text.secondary', bgcolor: isListening ? 'error.light' : 'transparent', '&:hover': { bgcolor: isListening ? 'error.light' : 'action.hover' } }}
                                >
                                    {isListening ? <MicOffIcon /> : <MicIcon />}
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="Send message">
                            <span>
                                <IconButton color="primary" onClick={() => sendMessage()} disabled={loading || (!input.trim() && !selectedFile)}>
                                    <SendIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
            </Paper>

            {/* Feedback Dialog */}
            <Dialog open={feedbackDialog.open} onClose={handleFeedbackDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ThumbDownIcon color="error" />
                        <Typography variant="h6">Help Us Improve</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        What could have been better about this response? (Optional)
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="e.g., The information was incorrect, I expected different data, The response was too long..."
                        value={feedbackComment}
                        onChange={(e) => setFeedbackComment(e.target.value)}
                        variant="outlined"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleFeedbackDialogSkip} color="inherit">Skip</Button>
                    <Button onClick={handleFeedbackDialogSubmit} variant="contained" color="primary">Submit Feedback</Button>
                </DialogActions>
            </Dialog>

            {/* Template Upload Dialog */}
            <Dialog open={templateDialog} onClose={handleTemplateDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CloudUploadIcon color="primary" />
                        <Typography variant="h6">Share a Report Template</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Upload an example of a report layout you like — a table format, a bulleted summary, a standard school report structure, or anything else. We'll use these to improve how ALPACA formats its answers in the future.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, fontStyle: 'italic' }}>
                        This won't change today's responses. Accepted formats: PDF, Word (.docx), CSV. Max 10MB.
                    </Typography>
                    <input
                        type="file"
                        accept=".pdf,.docx,.csv"
                        style={{ display: 'none' }}
                        ref={templateFileInputRef}
                        onChange={handleTemplateFileChange}
                    />
                    <Box sx={{ mb: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            onClick={() => templateFileInputRef.current.click()}
                            disabled={templateUploading}
                        >
                            {templateFile ? 'Change File' : 'Choose File'}
                        </Button>
                        {templateFile && (
                            <Chip
                                label={templateFile.name}
                                onDelete={() => { setTemplateFile(null); templateFileInputRef.current.value = null; }}
                                sx={{ ml: 1 }}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="What do you like about this layout? (optional)"
                        placeholder="e.g., I like how it groups students by score band, or the table makes it easy to scan quickly..."
                        value={templateNotes}
                        onChange={(e) => setTemplateNotes(e.target.value)}
                        variant="outlined"
                        disabled={templateUploading}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleTemplateDialogClose} color="inherit" disabled={templateUploading}>Cancel</Button>
                    <Button
                        onClick={handleTemplateUpload}
                        variant="contained"
                        color="primary"
                        disabled={!templateFile || templateUploading}
                        startIcon={templateUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                    >
                        {templateUploading ? 'Uploading...' : 'Upload Template'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AlpacaChatbot;